import type { Conversation, PartialConversation } from '../types';

type ParticipantIdentity =
    | { __typename: 'Professional'; Title: string }
    | { __typename: 'Member'; Name: string; FirstName: string };

/**
 * Get the participants of a chat, other than the current user.
 * @param conversation The conversation to get the participants from.
 * @param userID The ID of the current user.
 * @returns The participants of the chat, other than the current user.
 */
export function getChatParticipants(
    conversation: Conversation | PartialConversation | undefined,
    userID: number
) {
    if (!conversation) {
        return [];
    }
    if (conversation.__typename === 'EnquiryConversation') {
        return [conversation.InitialEnquiry.Professional];
    }
    return conversation.Participants.filter(
        ({ Identity }) =>
            Identity.__typename !== 'Member' || Identity.ID !== userID
    ).map(({ Identity }) => Identity);
}

/**
 * Generate the title of a chat based on the chat title and participants.
 * @param conversation The conversation to get the title for.
 * @param otherParticipants The participants of the chat, other than the current user.
 * @returns The title of the chat.
 */
export function getChatTitle(
    conversation: Conversation | PartialConversation | undefined,
    otherParticipants: ParticipantIdentity[]
): string {
    if (
        conversation?.__typename === 'EnquiryConversation' ||
        !conversation?.IsGroupChat ||
        otherParticipants.length === 1
    ) {
        // Is EnquiryConversation or a 1-on-1 user chat.
        const participant = otherParticipants[0]!;
        return participant.__typename === 'Professional'
            ? participant.Title
            : participant.Name;
    }
    // Is a group chat.
    if (conversation.Name) {
        return conversation.Name;
    }
    return otherParticipants
        .map((participant) => {
            return participant.__typename === 'Professional'
                ? participant.Title
                : participant.FirstName;
        })
        .join(', ');
}
